/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1682726400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Sat Apr 29 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "anaplan",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Creating a import data source in Anaplan",
          "depth": 0,
          "id": "creating-a-import-data-source-in-anaplan"
        }, {
          "value": "Creating an import in Anaplan",
          "depth": 0,
          "id": "creating-an-import-in-anaplan"
        }, {
          "value": "Creating a sync in Hightouch",
          "depth": 0,
          "id": "creating-a-sync-in-hightouch"
        }, {
          "value": "Selecting the model and import data source",
          "depth": 1,
          "id": "selecting-the-model-and-import-data-source"
        }, {
          "value": "Selecting the triggered imports",
          "depth": 1,
          "id": "selecting-the-triggered-imports"
        }, {
          "value": "Mapping your columns",
          "depth": 1,
          "id": "mapping-your-columns"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      img: "img",
      em: "em",
      ul: "ul",
      li: "li",
      h3: "h3"
    }, _provideComponents(), props.components), {PremiumDestination} = _components;
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Anaplan"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Anaplan destination, Hightouch can automate file uploads and trigger import actions in Anaplan.\nThis flexible integration allows you to import a variety of data from your warehouse, such as purchase orders, customer data, and more."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Records"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Anaplan records"
            }), _jsx(_components.td, {
              children: "Insert, All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.anaplan.com/5ef6eaab-72d9-43de-91b3-3f4dcc4711e2-Perform-an-import-action",
                children: "Import action docs"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Select the region of your Anaplan account then you can authenticate using basic auth (username and password)."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-authorize-opts.png",
          alt: "Authentication methods in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "You may need to create an IP whitelist for Hightouch servers to allow authentication"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["See how to create a whitelist on Anaplan ", _jsx(_components.a, {
            href: "https://help.anaplan.com/enable-ip-allow-list-9fcc01f4-fcb9-4384-8568-4ebb1bdfe9f9",
            children: "here"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["See Highotuch Server IP addresses ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/security/networking#ip-addresses",
            children: "here"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "creating-a-import-data-source-in-anaplan",
        children: "Creating a import data source in Anaplan"
      }), "\n", _jsx(_components.p, {
        children: "You must create an existing data source for Hightouch to upload data into. Navigate to your model -> Model settings -> Actions -> Import Data Source.\nCreate a new \"Import Data Source\" by uploading a placeholder CSV."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-import-data-source.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch integration will overwrite this CSV with data from your warehouse based on the mappings in your sync configuration."
      }), "\n", _jsx(_components.h2, {
        id: "creating-an-import-in-anaplan",
        children: "Creating an import in Anaplan"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally create imports based on the data source, and trigger them after the upload is complete in Hightouch. Navigate to a list and click the \"Import\" button.\nAfter selecting the placeholder data source, you can create an import that can then be selected in Hightouch and viewed in the imports page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-create-import.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "creating-a-sync-in-hightouch",
        children: "Creating a sync in Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "After setting up the data source and imports in the Anaplan destination, you can use Hightouch to automate sending data to the data sources and triggering\nthe import by creating a sync."
      }), "\n", _jsx(_components.h3, {
        id: "selecting-the-model-and-import-data-source",
        children: "Selecting the model and import data source"
      }), "\n", _jsx(_components.p, {
        children: "Select the model and data source that you created (or an existing one). Please keep in mind that Hightouch will overwrite the import data source. You can choose between two modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Insert: The data source is overwritten with only the rows that have been added since the last run."
        }), "\n", _jsx(_components.li, {
          children: "All: The data source is overwritten with the full results of the query in this run."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-model.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "selecting-the-triggered-imports",
        children: "Selecting the triggered imports"
      }), "\n", _jsx(_components.p, {
        children: "You can select one or more imports to trigger after the data source upload is complete. By default, Hightouch triggers these imports in parallel. Hightouch will wait until\nthe imports are complete before completing the sync."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-imports.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "mapping-your-columns",
        children: "Mapping your columns"
      }), "\n", _jsx(_components.p, {
        children: "You can map the columns in your Hightouch model to columns in your Anaplan data source."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The Anaplan column names you map to in Hightouch must exactly match the column\nnames configured your Anaplan data source. The columns must also be in the\nsame order as they appear in your data source. Incorrect column names or order\nmismatch will cause your sync to error."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-anaplan-mappings.png",
          alt: ""
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
